import { Cart, LineItem, Order, PaymentSession, Region } from '@medusajs/medusa'
import { LiteProductVariant } from '@ortros/stoux-lib'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useActorRef, useSelector } from '@xstate/react'
import { useAtom } from 'jotai'
import Cookies from 'js-cookie'
import {
    createContext,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { fromPromise, SnapshotFrom } from 'xstate'

import { queries } from '@/core/queries'
import { cartService } from '@/domain/Cart'
import { CreatePaymentSessionDto } from '@/domain/Cart/dtos/create-payment-session.dto'
import { SetPaymentSessionDto } from '@/domain/Cart/dtos/set-payment-session.dto'
import { isCartDrawerOpenAtom } from '@/shared/states/app'
import { MinimalCart, MinimalLineItem } from '@/shared/types/cart'
import eventEmitter from '@/utils/event-emitter'
import formatAmount from '@/utils/format-amount'
import noop from '@/utils/no-op'

import { useAccountContext } from '@/contexts/account-context'
import { useStore } from '@/contexts/store-context'
import { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import { Customer } from 'stoux-shared-types/models/customer'
import { cartMachine, CartMachineContext } from './Cart.machine'
import { pushEcommerceEvent } from '@/lib/gtm'

export type CartActorSnapshot = SnapshotFrom<typeof cartMachine>
export type CartActorSend = ReturnType<
    typeof useActorRef<typeof cartMachine>
>['send']

export type CartContextValue = {
    defaultRegion: Region | null
    hasCart: boolean
    hasItems: boolean
    isCartDrawerOpen: boolean
    items: Record<
        string,
        (LiteProductVariant & { template_barcode: string })[]
    > | null
    itemsCount: number
    total: string
    userLogged: Customer | null
    cartActor: [CartActorSnapshot, CartActorSend]
    onUpdateUserLogged: (user: Customer | null) => void
    onUpdateCart: (cart: Cart | null) => void
    onUpdateOrder: (order: Order | null) => void
    onUpdateOptions: (options: CartMachineContext['options']) => void
    onRemoveProductFromCart: (product: LiteProductVariant) => void
    onCloseCartDrawer: () => void
    setCanCloseCartDrawer: React.Dispatch<React.SetStateAction<boolean>>
    resetCart: () => void 
}

const initialValue: CartContextValue = {
    defaultRegion: null,
    hasCart: false,
    hasItems: false,
    isCartDrawerOpen: false,
    items: null,
    itemsCount: 0,
    total: '',
    userLogged: null,
    cartActor: {} as CartContextValue['cartActor'],
    onUpdateUserLogged: noop,
    onUpdateCart: noop,
    onUpdateOrder: noop,
    onUpdateOptions: noop,
    onRemoveProductFromCart: noop,
    onCloseCartDrawer: noop,
    setCanCloseCartDrawer: noop,
    resetCart: noop
}

export const CartContext = createContext<CartContextValue>(initialValue)

type CartProviderProps = {
    children: ReactNode
}

export const CartProvider = ({ children }: CartProviderProps) => {
    const { cart, deleteItem,resetCart } = useStore()

    const [canCloseCartDrawer, setCanCloseCartDrawer] = useState(true)

    const { customer, refetchCustomer } = useAccountContext()
    const [isCartDrawerOpen, setCartDrawerOpen] = useAtom(isCartDrawerOpenAtom)

    const regionsQuery = useQuery(queries.regions.listRegions())

    const defaultRegion = useMemo(
        () => regionsQuery.data?.find((reg) => reg) ?? null,
        [regionsQuery.data],
    )

    const createPaymentSession = useMutation({
        mutationFn: async (dto: CreatePaymentSessionDto) =>
            await cartService.createPaymentSession(dto),
    })

    const selectPaymentSession = useMutation({
        mutationFn: async (dto: SetPaymentSessionDto) =>
            await cartService.setPaymentSession(dto),
    })

    const actorRef = useActorRef(
        cartMachine.provide({
            actors: {
                          initializeCheckout: fromPromise<void, CartMachineContext>(
                    async ({ input: ctx }) => {
                        try {
                           
                            if (!defaultRegion) throw new Error('No default region found')
                            if (!cart) throw new Error('Cart not found')

                            if (ctx.options.anonymous_mode) {
                                // 👇 Plus de refetchCustomer, appel direct
                                const newCart = await initCartAnonymous({
                                    localCart: cart,
                                    anonymousEmail: ctx.options.anonymous_email ?? '',
                                    defaultRegion,
                                })
                                onUpdateCart(newCart)
                                return
                            }

                            // Seulement pour les users connectés
                            await refetchCustomer()
                            onUpdateCart(cart)
                        } catch (err) {
                            if (isAxiosError(err) && err.response?.status === 401 && ctx.options.anonymous_mode) {
                                throw { code: 'EMAIL_ALREADY_EXISTS' }
                            }
                            throw err
                        }
                    },
                ),
                syncShippingOptions: fromPromise<void, CartMachineContext>(
                    async ({ input: ctx }) => {
                        if (!ctx.cart) {
                            actorRef.send({
                                type: 'ERROR',
                                error: new Error(
                                    "Le panier n'existe pas.",
                                ),
                            })
                            return
                        }

                        const addShippingMethodPromises: Promise<Cart>[] = []

                        for (const shippingMethod of ctx.options
                            .shipping_methods) {
                            addShippingMethodPromises.push(
                                cartService.addShippingMethodForCart({
                                    cart_id: ctx.cart.id,
                                    shipping_method_id:
                                        shippingMethod.shipping_option.id,
                                    data: {
                                        store_id: shippingMethod.store_id,
                                        items: ctx.cart.items.map((item) => {
                                            const itemToPush = {
                                                id: item.id,
                                                store: (
                                                    item.variant
                                                        .product as typeof item.variant.product & {
                                                        storeId: string
                                                    }
                                                ).storeId,
                                            }
                                            return itemToPush
                                        }),
                                    },
                                }),
                            )
                        }

                        const addShippingMethodResponses = await Promise.all(
                            addShippingMethodPromises,
                        )

                        const latestUpdatedCart =
                            addShippingMethodResponses[
                                addShippingMethodResponses.length - 1
                            ]

                        onUpdateCart(latestUpdatedCart)
                    },
                ),
                createAndSelectPaymentSession: fromPromise<
                    void,
                    CartMachineContext
                >(async ({ input: ctx }) => {
                    if (!ctx.cart) {
                        actorRef.send({
                            type: 'ERROR',
                            error: new Error("Le panier n'existe pas."),
                        })
                        return
                    }

                    const updatedCart =
                        await createPaymentSession.mutateAsync({
                            cart_id: ctx.cart.id,
                        })

                    onUpdateCart(updatedCart)

                    const defaultPaymentSession =
                        updatedCart.payment_sessions.find(
                            (ps) => ps.provider_id === 'stripe',
                        )

                    if (!defaultPaymentSession) {
                        throw new Error('No payment session found')
                    }

                    await selectPaymentSession.mutateAsync({
                        cart_id: ctx.cart.id,
                        provider_id: defaultPaymentSession.provider_id,
                    })

                    onUpdatePaymentSession(defaultPaymentSession)
                }),
            },
            guards: {
                isUserLoggedIn: () => !!customer,
            },
        }),
    )

    const snapshot = useSelector(actorRef, (s) => s)

    const [itemsWithVariants, setItemsWithVariants] = useState<
        (LiteProductVariant & { template_barcode: string })[]
    >([])

    const _items = useMemo(() => {
        return cart?.items || []
    }, [cart])

    const _itemsWithVariantsGroupedByVendor = useMemo(() => {
        const array = itemsWithVariants.reduce(
            (acc, item) => {
                const { store_id } = item

                if (!acc[store_id]) {
                    acc[store_id] = []
                }

                acc[store_id].push(item)

                return acc
            },
            {} as Record<
                string,
                (LiteProductVariant & { template_barcode: string })[]
            >,
        )

        return array
    }, [itemsWithVariants])

    const _itemsCount = _items.length

    const _total = formatAmount(
        _items.reduce((acc, item) => {
            const { unit_price } = item

            return acc + unit_price
        }, 0),
    )

    const onRemoveProductFromCart = useCallback(
        async (product: LiteProductVariant) => {
            const itemFound = _items.find(
                (item) => item.variant_id === product.id,
            )

            if (!itemFound) {
                return
            }

            try {
                deleteItem(itemFound.id)
            } catch (error) {
                toast.error("Une erreur s'est produite lors de la suppression.")
            }
        },
        [_items, deleteItem],
    )

    /**
     * ? Update user logged state
     */
    const onUpdateUserLogged = useCallback(
        async (user: Customer | null) => {
            await refetchCustomer()
        },
        [refetchCustomer],
    )

    /**
     * ? Update context value : cart inside the _cartMachine
     */
    const onUpdateCart = useCallback(
        (cart: Omit<Cart, 'refundable_amount' | 'refunded_total'> | null) => {
            actorRef.send({
                type: 'UPDATE_CART',
                updatedCart: cart as Cart | null,
            })
        },
        [actorRef],
    )

    /**
     * ? Update context value : order inside the _cartMachine
     */
    const onUpdateOrder = useCallback(
        (order: Order | null) => {
            actorRef.send({ type: 'UPDATE_ORDER', updatedOrder: order })
        },
        [actorRef],
    )

    /**
     * ? Update context value : options inside the _cartMachine
     */
    const onUpdateOptions = useCallback(
        (options: CartMachineContext['options']) => {
            actorRef.send({ type: 'UPDATE_OPTIONS', updatedOptions: options })
        },
        [actorRef],
    )

    /**
     * ? Update context value : payment session inside the _cartMachine
     */
    const onUpdatePaymentSession = useCallback(
        (paymentSession: PaymentSession | null) => {
            actorRef.send({
                type: 'UPDATE_PAYMENT_SESSION',
                updatedPaymentSession: paymentSession,
            })
        },
        [actorRef],
    )

    /**
     * Listen to TOGGLE_CART_DRAWER event
     */
    useEffect(() => {
        const _onToggleCartDrawer = () => {
            setCartDrawerOpen(!isCartDrawerOpen)
        }

        eventEmitter.addListener('TOGGLE_CART_DRAWER', _onToggleCartDrawer)
        eventEmitter.addListener(
            'PRODUCT_REMOVED_FROM_CART',
            onRemoveProductFromCart,
        )

        return () => {
            eventEmitter.removeListener(
                'TOGGLE_CART_DRAWER',
                _onToggleCartDrawer,
            )
            eventEmitter.removeListener(
                'PRODUCT_REMOVED_FROM_CART',
                onRemoveProductFromCart,
            )
        }
    }, [isCartDrawerOpen, onRemoveProductFromCart, setCartDrawerOpen])

    /**
     * Fetch variants from cart items when cart drawer is open
     */
    useEffect(() => {
        if (!isCartDrawerOpen) {
            return
        }

        const fetchVariants = async () => {
            if (!defaultRegion) {
                console.error('No default region found')
                return
            }

            try {
                const _itemsWithVariants: (LiteProductVariant & {
                    template_barcode: string
                })[] = []
                for (const item of _items) {
                    const variant = await cartService.getVariantById({
                        variant_id: item.variant_id ?? '',
                    })

                    if (!variant.metadata) {
                        deleteItem(item.id)
                        throw new Error('No metadata found')
                    }

                    if (!variant.metadata.id || variant.metadata.id === '') {
                        throw new Error('No metadata id found')
                        return
                    }

                    // ADD DEFAULT REGION TAXES
                    const taxRate = defaultRegion?.tax_rate ?? 0

                    variant.metadata.price_amount =
                        variant.metadata.price_amount +
                        (variant.metadata.price_amount * taxRate) / 100

                    _itemsWithVariants.push(variant.metadata!)
                }

                setItemsWithVariants(_itemsWithVariants)
            } catch (err) {
                if (isAxiosError(err)) {
                    if (err.response?.status === 403) {
                        actorRef.send({
                            type: 'ERROR',
                            error: new Error('Forbidden'),
                        })
                        localStorage.removeItem('cart')
                        onUpdateCart(null)
                        actorRef.send({ type: 'RESET' })
                    }
                }
                actorRef.send({
                    type: 'ERROR',
                    error: new Error('Fetch variants failed'),
                })
                localStorage.removeItem('cart')
                onUpdateCart(null)
                actorRef.send({ type: 'RESET' })
            }
        }

        fetchVariants()
    }, [_items, _itemsCount, _total, defaultRegion, isCartDrawerOpen, actorRef, deleteItem, onUpdateCart])

    /**
     * ? Reset cart machine when cart drawer is closed
     */
    useEffect(() => {
           if (!isCartDrawerOpen) {
       
            const isInCheckout = snapshot.matches('CHECKOUT') || 
                                snapshot.matches('AUTH_GATEWAY')
            
            if (!isInCheckout) {
                actorRef.send({ type: 'RESET' })
            }
            return
        }

        if (_items.length > 0) {
            

            pushEcommerceEvent('view_cart', {
                currency: 'EUR',
                value: _items.reduce((acc, item) => acc + item.unit_price, 0) / 100,
                items: _items.filter((item) => item?.variant?.product != null).map((item) => ({
                    item_id: item.variant.barcode,
                    item_name: item.variant.product.title,
                    price: item.unit_price / 100,
                    quantity: item.quantity,
                }))
            })
        }

    }, [isCartDrawerOpen, _items, actorRef])

    const contextValue: CartContextValue = {
        defaultRegion,
        userLogged: customer,
        hasCart: !!snapshot.context.cart,
        hasItems: _itemsCount > 0,
        isCartDrawerOpen,
        items: _itemsWithVariantsGroupedByVendor,
        itemsCount: _itemsCount,
        cartActor: [snapshot, actorRef.send.bind(actorRef)],
        total: _total,
        onUpdateUserLogged,
        onUpdateCart,
        onUpdateOrder,
        onUpdateOptions,
        onRemoveProductFromCart,
        resetCart,
        onCloseCartDrawer: () => {
            if (!canCloseCartDrawer) return

            if (
                snapshot.matches({ CHECKOUT: { PAYMENT: 'INITIALIZING' } }) ||
                snapshot.matches({ CHECKOUT: { DETAILS: 'SYNCING' } })
            ) {
                return
            }

            // Remettre la machine à l'état initial pour qu'à la prochaine ouverture
            // le tunnel reparte depuis le début
            actorRef.send({ type: 'RESET' })
            setCartDrawerOpen(false)
        },
        setCanCloseCartDrawer,
    }

    return (
        <CartContext.Provider value={contextValue}>
            {children}
        </CartContext.Provider>
    )
}

type InitMachineProps = {
    localCart: MinimalCart
    userLogged: Customer | null
    defaultRegion: Region
}

/**
 *  # initCart
 *  ## Explanation
 *  Initialize the machine by creating a new cart or updated the cart.
 *  Then the function returns the cart so the checkout flow can start.
 *
 *  ## Note
 * This is only for auth users.
 */
export async function initCart({
    localCart,
    userLogged,
    defaultRegion,
}: InitMachineProps): Promise<Cart> {
    try {
        const cartID: string | null = Cookies.get('cart_id') ?? null

        // ? If there is no cookie "cart_id", create a new cart
        if (!cartID) {
            const newCart: Cart = await cartService.createCart({
                region_id: defaultRegion.id,
                country_code: 'FR',
                items: localCart?.items.map((item) => ({
                    variant_id: item.variant_id!,
                    quantity: Number(item.quantity),
                })),
            })
            Cookies.set('cart_id', newCart.id)
            return newCart
        }

        if (!userLogged) {
            throw new Error('User not logged')
        }

        // ? If there is a cookie, fetch the cart
        const foundCart: Cart = await cartService.getCartById({
            cart_id: cartID,
        })

        if (!foundCart) {
            throw new Error('Cart not found')
        }

        // ? Update the cart
        const updatedCart: Cart = await cartService.updateCart({
            cart_id: foundCart.id,
            region_id: defaultRegion.id,
            country_code: 'FR',
            customer_id: userLogged.id,
        })
        Cookies.set('cart_id', updatedCart.id)

        // ? Add / Update line items of the cart
        const itemsToAdd: MinimalLineItem[] = localCart?.items.filter(
            (item) => {
                const foundItem = foundCart.items.find(
                    (i) => i.variant_id === item.variant_id,
                )
                return !foundItem
            },
        )

        const itemsToUpdate: LineItem[] = foundCart.items.filter((item) => {
            const foundItem = localCart?.items.find(
                (i) => i.variant_id === item.variant_id,
            )
            return foundItem && foundItem.quantity !== item.quantity
        })

        if (itemsToAdd?.length) {
            for (const lineItem of itemsToAdd) {
                await cartService.addLineItem({
                    cart_id: foundCart.id,
                    variant_id: lineItem.variant_id!,
                    quantity: Number(lineItem.quantity),
                }, foundCart)
            }
        }

        if (itemsToUpdate?.length) {
            for (const lineItem of itemsToUpdate) {
                await cartService.updateLineItem({
                    cart_id: foundCart.id,
                    line_id: lineItem.id,
                    quantity: Number(lineItem.quantity),
                })
            }
        }

        return updatedCart
    } catch (err) {
        console.error(err)
        throw err
    }
}

type InitMachineAnonymousProps = Omit<InitMachineProps, 'userLogged'> & {
    anonymousEmail: string
}

/**
 *  # initCartAnonymous
 *  ## Explanation
 *  Initialize the machine by creating a new cart or updated the cart.
 *  Then the function returns the cart so the checkout flow can start.
 *
 *  ## Note
 * This is only for anonymous users.
 */
export async function initCartAnonymous({
    localCart,
    defaultRegion,
    anonymousEmail,
}: InitMachineAnonymousProps): Promise<Cart> {
    try {
        if (!anonymousEmail) {
            throw new Error('Anonymous email not provided')
        }

        const cartID: string | null = Cookies.get('cart_id') ?? null

        // ? If there is no cookie "cart_id", create a new cart
        if (!cartID) {
            const newCart: Cart = await cartService.createCart({
                region_id: defaultRegion.id,
                country_code: 'FR',
                items: localCart?.items.map((item) => ({
                    variant_id: item.variant_id!,
                    quantity: Number(item.quantity),
                })),
            })

            // ? The create-cart endpoint does not accept an email, so the
            // guest customer is only attached to the cart via this update.
            const updatedCart: Cart = await cartService.updateCart({
                cart_id: newCart.id,
                email: anonymousEmail,
            })

            Cookies.set('cart_id', updatedCart.id)
            return updatedCart
        }

        // ? If there is a cookie, fetch the cart
        const foundCart: Cart = await cartService.getCartById({
            cart_id: cartID,
        })

        if (!foundCart) {
            throw new Error('Cart not found')
        }

        // ? Update the cart
        const updatedCart: Cart = await cartService.updateCart({
            cart_id: foundCart.id,
            region_id: defaultRegion.id,
            country_code: 'FR',
            email: anonymousEmail,
        })
        Cookies.set('cart_id', updatedCart.id)

        // ? Add / Update line items of the cart
        const itemsToAdd: MinimalLineItem[] = localCart?.items.filter(
            (item) => {
                const foundItem = foundCart.items.find(
                    (i) => i.variant_id === item.variant_id,
                )
                return !foundItem
            },
        )

        const itemsToUpdate: LineItem[] = foundCart.items.filter((item) => {
            const foundItem = localCart?.items.find(
                (i) => i.variant_id === item.variant_id,
            )
            return foundItem && foundItem.quantity !== item.quantity
        })

        if (itemsToAdd?.length) {
            for (const lineItem of itemsToAdd) {
                await cartService.addLineItem({
                    cart_id: foundCart.id,
                    variant_id: lineItem.variant_id!,
                    quantity: Number(lineItem.quantity),
                }, foundCart)
            }
        }

        if (itemsToUpdate?.length) {
            for (const lineItem of itemsToUpdate) {
                await cartService.updateLineItem({
                    cart_id: foundCart.id,
                    line_id: lineItem.id,
                    quantity: Number(lineItem.quantity),
                })
            }
        }

        return updatedCart
    } catch (err) {
        console.error(err)
        throw err
    }
}

function isAxiosError(error: any): error is AxiosError {
    return error?.isAxiosError !== undefined
}
